import { all, call, put, select } from 'redux-saga/effects';
import { organizationSelector } from 'selectors/Organization';
import { projectSelector } from 'selectors/Projects';
import { Project } from 'models/Project';
import { Organization } from 'models/Organization';
import { fetchOrganizationMembers } from '../organizationMembers';
import { fetchMissingMembers } from '../organizations';

import { getContributionVolume } from './ContributionVolume';
import { getProjectBreakdown } from './ProjectBreakdown';
import { getProjectStats } from './ProjectStats';
import { getReviewLifecycleDuration } from './ReviewLifecycleDuration';
import { getMetricsHighlights } from './MetricsHighlights';
import { getStaleBranches } from './StaleBranches';

import { actions as contributionVolumeActions } from 'reducers/ProjectDashboard/ContributionVolume';
import { actions as authorHighlightsActions } from 'reducers/ProjectDashboard/Highlights/AuthorHighlights';
import { actions as reviewerHighlightsActions } from 'reducers/ProjectDashboard/Highlights/ReviewerHighlights';
import { actions as projectBreakdownActions } from 'reducers/ProjectDashboard/ProjectBreakdown';
import { actions as projectStatActions } from 'reducers/ProjectDashboard/ProjectStats';
import { actions as reviewLifecycleActions } from 'reducers/ProjectDashboard/ReviewLifecycleDuration';
import { actions as staleBranchesActions } from 'reducers/ProjectDashboard/StaleBranches';

export function* fetchProjectDashboardData() {
  try {
    const project: Project = yield select(projectSelector);
    const organization: Organization = yield select(organizationSelector);

    yield call(resetDashboard);

    const { uuid: organizationUUID } = organization;
    const { uuid: repositoryUUID } = project;
    yield call(refreshDashboardData, organizationUUID, repositoryUUID);
  } catch {
    console.error('An error occurred in the fetchProjectDashboardData saga');
  }
}

export function* resetDashboard() {
  yield all([
    put(projectBreakdownActions.reset()),
    put(contributionVolumeActions.reset()),
    put(authorHighlightsActions.resetAuthorHighlights()),
    put(reviewerHighlightsActions.resetReviewerHighlights()),
    put(projectStatActions.reset()),
    put(reviewLifecycleActions.reset()),
    put(staleBranchesActions.reset()),
  ]);
}

export function* refreshDashboardData(
  organizationUUID: string,
  repositoryUUID: string
) {
  /**
   * This data is needed when displaying the highlights widgets. Fetching this first so that there is no race condition
   * where the data isn't yet populated before trying to access in down stream sagas.
   */
  yield all([
    fetchOrganizationMembers({
      organizationUuid: organizationUUID,
    }),
    fetchMissingMembers({
      organizationUuid: organizationUUID,
    }),
  ]);

  yield all([
    getProjectBreakdown({
      repositoryUUID,
    }),
    getContributionVolume({
      organizationUUID,
      repositoryUUID,
    }),
    getProjectStats({
      repositoryUUID,
      organizationUUID,
    }),
    getStaleBranches({
      repositoryUUID,
    }),
    getReviewLifecycleDuration({
      organizationUUID,
      repositoryUUID,
    }),
    getMetricsHighlights({
      organizationUUID,
      repositoryUUID,
    }),
  ]);
}
